/*
 * Figma `95:2` — Pagination and the result summary the v3 tables carry.
 *
 * `TableFooter` already covers the feed-shaped lists that only ever grow downwards. This is the
 * other half: the registries and operational queues where someone needs to know how much there is,
 * where they are in it, and how to get to the end — Transactions, Audit Log, Slip Review.
 *
 * The summary is not optional garnish. A filtered table that says "แสดง 1–25 จาก 412" is the only
 * thing that distinguishes "your filter is narrow" from "the query failed and returned a page", and
 * the frozen design requires the filtered-empty and partial-result states to be legible.
 *
 * `aria-current="page"` marks where you are; the surrounding `nav` gets a name because a page with
 * two tables has two of these and "navigation" twice over helps nobody.
 */
import { Button } from './Button';
import { IconChevronLeft, IconChevronRight } from './icons';

export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Total matching rows, after filters. */
  total: number;
  onPageChange: (page: number) => void;
  /** Names the nav landmark, e.g. "ธุรกรรม". */
  label: string;
  busy?: boolean;
}

/**
 * Page numbers to draw: always the first and last, always the current and its neighbours, with a
 * gap marker standing in for whatever that skips. Ten pages fit; four hundred do not.
 */
export function pageWindow(page: number, pageCount: number): (number | 'gap')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const near = [page - 1, page, page + 1].filter((p) => p > 1 && p < pageCount);
  const pages = [1, ...near, pageCount];
  const out: (number | 'gap')[] = [];
  for (let i = 0; i < pages.length; i += 1) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) out.push('gap');
    out.push(pages[i]);
  }
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange, label, busy = false }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="jt-pagination">
      <p className="jt-pagination-summary" aria-live="polite">
        {total === 0 ? 'ไม่พบรายการที่ตรงกับตัวกรอง' : `แสดง ${first}–${last} จาก ${total.toLocaleString('th-TH')}`}
      </p>
      {pageCount > 1 && (
        <nav className="jt-pagination-pages" aria-label={`หน้าของ${label}`}>
          <Button
            size="sm" variant="ghost" iconOnly icon={<IconChevronLeft size={15} />}
            aria-label="หน้าก่อนหน้า"
            disabled={busy || page <= 1}
            onClick={() => onPageChange(page - 1)}
          />
          {pageWindow(page, pageCount).map((entry, i) =>
            entry === 'gap' ? (
              <span key={`gap-${i}`} className="jt-pagination-gap" aria-hidden="true">…</span>
            ) : (
              <Button
                key={entry}
                size="sm"
                variant={entry === page ? 'secondary' : 'ghost'}
                aria-label={`หน้า ${entry}`}
                aria-current={entry === page ? 'page' : undefined}
                disabled={busy}
                onClick={() => onPageChange(entry)}
              >
                {entry}
              </Button>
            ),
          )}
          <Button
            size="sm" variant="ghost" iconOnly icon={<IconChevronRight size={15} />}
            aria-label="หน้าถัดไป"
            disabled={busy || page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          />
        </nav>
      )}
    </div>
  );
}
